import { QRCodeCanvas } from "qrcode.react";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";

import {
  Box,
  Button,
  // Checkbox,
  createTheme,
  Divider,
  Grid2,
  Stack,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";

import {
  IconArrowLeft,
  IconArrowRight,
  IconBuildingStore,
  IconCopy,
  IconCreditCard,
  IconCurrencyReal,
  IconExternalLink,
  IconLock,
  IconWallet,
} from "@tabler/icons-react";

import ContainerLoading from "../components/ContainerLoading";
import { Input } from "../components/Input";

import {
  checkoutCreditCard,
  checkoutListPayment,
  renewPixPayment,
} from "../services/checkout";
import { initializeSocket } from "../services/socket";

import {
  formatCpfCnpj,
  formatDate,
  formatPaymentStatus,
  formatPrice,
} from "../helpers/format";

import { toast } from "react-toastify";
import logo from "../assets/svg/logo.svg";
import BoxLoading from "../components/BoxLoading";
import baseTheme from "../theme/theme";
import moment from "moment";

// ── Mercado Pago mp.fields (Core Methods) ────────────────────────────────────

declare global {
  interface Window {
    MercadoPago: any;
  }
}

const loadMpScript = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (window.MercadoPago) return resolve();
    const s = document.createElement("script");
    s.src = "https://sdk.mercadopago.com/js/v2";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Falha ao carregar SDK do MP"));
    document.head.appendChild(s);
  });

interface MpCardFormProps {
  publicKey: string;
  amount: number;
  maxInstallments: number;
  showSaveCreditCard: boolean;
  saveCreditCard: boolean;
  onToggleSaveCreditCard: () => void;
  onSubmit: (params: {
    cardToken: string;
    paymentMethodId: string;
    issuerId: string;
    installments: number;
    saveCreditCard: boolean;
    cardholderName: string;
    lastFourDigits: string;
    expirationMonth: string;
    expirationYear: string;
    deviceId: string;
    payerEmail: string;
  }) => void;
  defaultEmail?: string;
  isPending: boolean;
}

const MpCardForm: React.FC<MpCardFormProps> = ({
  publicKey,
  amount,
  maxInstallments,
  // showSaveCreditCard,
  saveCreditCard,
  // onToggleSaveCreditCard,
  onSubmit,
  isPending,
  defaultEmail = "",
}) => {
  const mpRef = useRef<any>(null);
  const fieldsRef = useRef<any>(null);

  const [cardholderName, setCardholderName] = useState("");
  const [payerEmail, setPayerEmail] = useState(defaultEmail);
  const [docNumber, setDocNumber] = useState("");
  const [paymentMethodId, setPaymentMethodId] = useState("");
  const [issuerId, setIssuerId] = useState("");
  const [installments, setInstallments] = useState(1);
  const [installmentOptions, setInstallmentOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [issuerOptions, setIssuerOptions] = useState<
    { id: string; name: string }[]
  >([]);
  const [docType, setDocType] = useState("CPF");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      await loadMpScript();
      const mp = new window.MercadoPago(publicKey, { locale: "pt-BR" });
      mpRef.current = mp;

      const cardNumberField = mp.fields
        .create("cardNumber", { placeholder: "0000 0000 0000 0000" })
        .mount("mp-card-number");

      const expirationDateField = mp.fields
        .create("expirationDate", { placeholder: "MM/AA" })
        .mount("mp-expiration-date");

      const securityCodeField = mp.fields
        .create("securityCode", { placeholder: "CVV" })
        .mount("mp-security-code");

      fieldsRef.current = {
        cardNumber: cardNumberField,
        expirationDate: expirationDateField,
        securityCode: securityCodeField,
      };

      cardNumberField.on("binChange", async ({ bin }: { bin: string }) => {
        if (!mounted) return;
        if (!bin || bin.length < 8) {
          setPaymentMethodId("");
          setIssuerId("");
          setInstallmentOptions([]);
          setIssuerOptions([]);
          return;
        }
        try {
          const { results } = await mp.getPaymentMethods({ bin });
          const pm = results?.[0];
          if (!pm) return;
          setPaymentMethodId(pm.id);

          // Atualiza settings dos iframes conforme regras da bandeira (melhora validação)
          if (pm.settings?.[0]) {
            fieldsRef.current?.cardNumber?.update?.({
              settings: pm.settings[0].card_number,
            });
            fieldsRef.current?.securityCode?.update?.({
              settings: pm.settings[0].security_code,
            });
          }

          let issuers: any[] = [pm.issuer];
          if (pm.additional_info_needed?.includes("issuer_id")) {
            issuers = await mp.getIssuers({ paymentMethodId: pm.id, bin });
          }
          const mappedIssuers = issuers.map((i: any) => ({
            id: String(i.id),
            name: i.name,
          }));
          setIssuerOptions(mappedIssuers);
          if (issuers[0]) setIssuerId(String(issuers[0].id));

          const installmentsData = await mp.getInstallments({
            amount: String(amount),
            bin,
            paymentTypeId: "credit_card",
          });
          const payerCosts: any[] = installmentsData?.[0]?.payer_costs ?? [];
          const filtered = payerCosts
            .filter((p) => p.installments <= maxInstallments)
            .filter((p) => amount / p.installments >= 10);
          setInstallmentOptions(
            filtered.map((p) => ({
              label: p.recommended_message,
              value: p.installments,
            })),
          );
          if (filtered[0]) setInstallments(filtered[0].installments);
        } catch (e) {
          console.error("binChange error:", e);
        }
      });
    };

    init().catch(console.error);

    return () => {
      mounted = false;
      try {
        fieldsRef.current?.cardNumber?.unmount?.();
        fieldsRef.current?.expirationDate?.unmount?.();
        fieldsRef.current?.securityCode?.unmount?.();
      } catch {}
    };
  }, [publicKey]);

  const handlePay = async () => {
    const mp = mpRef.current;
    if (!mp || !fieldsRef.current || submitting) return;
    setSubmitting(true);
    try {
      const token = await mp.fields.createCardToken({
        cardholderName,
        identificationType: docType,
        identificationNumber: docNumber.replace(/\D/g, ""),
      });
      if (!token?.id)
        throw new Error("Token inválido: " + JSON.stringify(token));
      const deviceId = mpRef.current?.getDeviceId?.() ?? "";
      onSubmit({
        cardToken: token.id,
        paymentMethodId,
        issuerId,
        installments,
        saveCreditCard,
        cardholderName,
        lastFourDigits: token.last_four_digits ?? "",
        expirationMonth: String(token.expiration_month ?? ""),
        expirationYear: String(token.expiration_year ?? ""),
        deviceId,
        payerEmail,
      });
    } catch (err: any) {
      toast.error(
        err?.message ??
          "Erro ao tokenizar cartão. Verifique os dados do cartão e tente novamente.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const iframeContainerSx = {
    border: "1px solid rgba(0,0,0,0.23)",
    borderRadius: "4px",
    height: "48px",
    padding: "13px 14px",
    backgroundColor: "#fff",
    "&:hover": { borderColor: "rgba(0,0,0,0.87)" },
  };

  return (
    <Box sx={{ padding: "5px" }}>
      <Grid2 container spacing={2}>
        {/* Card number — iframe MP */}
        <Grid2 size={12}>
          <Typography fontSize={12} color="#575757" mb={0.5}>
            Número do cartão
          </Typography>
          <Box id="mp-card-number" sx={iframeContainerSx} />
        </Grid2>

        {/* Expiry + CVV — iframes MP */}
        <Grid2 size={6}>
          <Typography fontSize={12} color="#575757" mb={0.5}>
            Validade
          </Typography>
          <Box id="mp-expiration-date" sx={iframeContainerSx} />
        </Grid2>
        <Grid2 size={6}>
          <Typography fontSize={12} color="#575757" mb={0.5}>
            CVV
          </Typography>
          <Box id="mp-security-code" sx={iframeContainerSx} />
        </Grid2>

        {/* Cardholder name */}
        <Grid2 size={12}>
          <Input
            label="Nome igual no cartão"
            fullWidth
            sx={{ "& label": { color: "#7600ED" } }}
            value={cardholderName}
            onChange={(e: any) => setCardholderName(e.target.value)}
          />
        </Grid2>

        {/* Email */}
        <Grid2 size={12}>
          <Input
            label="E-mail"
            type="email"
            fullWidth
            sx={{ "& label": { color: "#7600ED" } }}
            value={payerEmail}
            onChange={(e: any) => setPayerEmail(e.target.value)}
          />
        </Grid2>

        {/* CPF / CNPJ — tipo detectado automaticamente pelo número de dígitos */}
        <Grid2 size={12}>
          <Input
            label={`Documento do responsável pelo cartão`}
            fullWidth
            sx={{ "& label": { color: "#7600ED" } }}
            value={
              docType === "CPF"
                ? docNumber
                    .replace(/(\d{3})(\d)/, "$1.$2")
                    .replace(/(\d{3})(\d)/, "$1.$2")
                    .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
                : docNumber
                    .replace(/(\d{2})(\d)/, "$1.$2")
                    .replace(/(\d{3})(\d)/, "$1.$2")
                    .replace(/(\d{3})(\d)/, "$1/$2")
                    .replace(/(\d{4})(\d{1,2})$/, "$1-$2")
            }
            onChange={(e: any) => {
              const digits = e.target.value.replace(/\D/g, "").slice(0, 14);
              setDocNumber(digits);
              if (digits.length <= 11) {
                setDocType("CPF");
              } else {
                setDocType("CNPJ");
              }
            }}
            inputProps={{ maxLength: 18 }}
            helperText={
              docNumber.length > 0 && docNumber.length < 11
                ? "CPF incompleto (11 dígitos)"
                : docNumber.length > 11 && docNumber.length < 14
                  ? "CNPJ incompleto (14 dígitos)"
                  : docNumber.length === 11
                    ? "CPF detectado ✓"
                    : docNumber.length === 14
                      ? "CNPJ detectado ✓"
                      : ""
            }
            FormHelperTextProps={{
              sx: {
                color:
                  docNumber.length === 11 || docNumber.length === 14
                    ? "#2E8B57"
                    : "#B22222",
              },
            }}
          />
        </Grid2>

        {/* Issuer — só aparece se o banco retornar mais de 1 opção */}
        {issuerOptions.length > 1 && (
          <Grid2 size={12}>
            <Typography fontSize={12} color="#575757" mb={0.5}>
              Banco emissor
            </Typography>
            <select
              value={issuerId}
              onChange={(e) => setIssuerId(e.target.value)}
              style={{
                width: "100%",
                padding: "13px 14px",
                border: "1px solid rgba(0,0,0,0.23)",
                borderRadius: 4,
                fontSize: 14,
                backgroundColor: "#fff",
                cursor: "pointer",
              }}
            >
              {issuerOptions.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </select>
          </Grid2>
        )}

        {/* Parcelas — aparecem após detectar bandeira */}
        {installmentOptions.length > 0 && (
          <Grid2 size={12}>
            <Typography fontSize={12} color="#575757" mb={0.5}>
              Parcelas
            </Typography>
            <select
              value={installments}
              onChange={(e) => setInstallments(Number(e.target.value))}
              style={{
                width: "100%",
                padding: "13px 14px",
                border: "1px solid rgba(0,0,0,0.23)",
                borderRadius: 4,
                fontSize: 14,
                backgroundColor: "#fff",
                cursor: "pointer",
              }}
            >
              {installmentOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Grid2>
        )}

        {/* Salvar cartão
        {showSaveCreditCard && (
          <Grid2 size={12}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Checkbox
                sx={{ color: "#7600ED" }}
                checked={saveCreditCard}
                onClick={onToggleSaveCreditCard}
              />
              <Typography color="black">
                Usar cartão para cobranças futuras
              </Typography>
            </Stack>
          </Grid2>
        )} */}

        <Grid2 size={12}>
          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 1, backgroundColor: "#7600ED" }}
            startIcon={<IconCreditCard />}
            onClick={handlePay}
            disabled={
              isPending ||
              submitting ||
              !cardholderName ||
              (docNumber.length !== 11 && docNumber.length !== 14) ||
              !payerEmail
            }
          >
            {isPending || submitting ? "Processando..." : "Pagar"}
          </Button>
        </Grid2>
      </Grid2>
    </Box>
  );
};

const checkoutTheme = createTheme({
  ...baseTheme,
  components: {
    ...baseTheme.components,

    MuiInputBase: {
      ...baseTheme.components?.MuiInputBase,
      styleOverrides: {
        ...baseTheme.components?.MuiInputBase?.styleOverrides,
        root: (params: any) => {
          const originalRoot =
            typeof baseTheme.components?.MuiInputBase?.styleOverrides?.root ===
            "function"
              ? baseTheme.components.MuiInputBase.styleOverrides.root(params)
              : baseTheme.components?.MuiInputBase?.styleOverrides?.root;

          const safeOriginalRoot =
            originalRoot && typeof originalRoot === "object"
              ? originalRoot
              : {};

          return {
            ...safeOriginalRoot,
            backgroundColor: "#FFF",
            color: "#000",
          };
        },
      },
    },

    MuiInputLabel: {
      ...baseTheme.components?.MuiInputLabel,
      styleOverrides: {
        ...baseTheme.components?.MuiInputLabel?.styleOverrides,
        root: (params: any) => {
          const originalRoot =
            typeof baseTheme.components?.MuiInputLabel?.styleOverrides?.root ===
            "function"
              ? baseTheme.components.MuiInputLabel.styleOverrides.root(params)
              : baseTheme.components?.MuiInputLabel?.styleOverrides?.root;

          const safeOriginalRoot =
            originalRoot && typeof originalRoot === "object"
              ? originalRoot
              : {};

          return {
            ...safeOriginalRoot,
            color: "#000",
            "&.Mui-focused": {
              color: "#000",
            },
            "&.MuiInputLabel-shrink": {
              color: "#000",
            },
          };
        },
      },
    },
  },
});

const Checkout = () => {
  const param: any = useParams();

  const [saveCreditCard, setSaveCreditCard] = useState(false);
  const [enableCreditCard, setEnableCreditCard] = useState(false);
  const [fullScreenPayment, setFullScreenPayment] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState("");

  // Ref para detectar transição de status → confirmado
  const prevStatusRef = useRef<string | undefined>(undefined);

  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ["checkoutListPayment", param?.webToken],
    queryFn: async () => await checkoutListPayment(param?.webToken),
  });

  const { mutate, isPending } = useMutation({
    mutationFn: checkoutCreditCard,
    onSuccess: () => {
      toast.info("Pagamento enviado! Aguardando confirmação do banco...");
      refetch();
      setFullScreenPayment(false);
      setSelectedPayment("");
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ??
          "Não conseguimos processar seu pagamento, entre em contato com seu cartão de crédito",
      );
    },
  });

  const { mutate: regeneratePix, isPending: isRegeneratingPix } = useMutation({
    mutationFn: () => renewPixPayment(param?.webToken),
    onSuccess: () => {
      toast.info("Novo QR Code gerado!");
      refetch();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? "Erro ao gerar novo Pix.");
      setSelectedPayment("");
    },
  });

  const handleSelectPix = () => {
    const PIX_EXPIRATION_MINUTES = 60;
    const pixCreatedAt = data?.lastUpdatePixDate ?? data?.createdAt;
    const isExpired = pixCreatedAt
      ? moment(pixCreatedAt)
          .add(PIX_EXPIRATION_MINUTES, "minutes")
          .isBefore(moment())
      : true;
    const hasValidPix = !!data?.bankPixCode && !isExpired;

    setSelectedPayment("PIX");

    if (hasValidPix) {
      setFullScreenPayment(true);
    } else {
      regeneratePix(undefined, {
        onSuccess: () => {
          setFullScreenPayment(true);
        },
      });
    }
  };

  const store = data?.store;

  const getColor = () => {
    if (
      [
        "CONFIRMED",
        "CONFIRMED_INSTALLMENT",
        "PAID",
        "RECEIVED_IN_CASH",
      ].includes(data?.status)
    ) {
      return "#2E8B57";
    }

    if (["OVERDUE"].includes(data?.status)) {
      return "#B22222";
    }

    return "#ffc400";
  };

  useEffect(() => {
    if (data) {
      if (data?.studentOnPlan) {
        const isCreditCard = data?.studentOnPlan?.plan
          ? data?.studentOnPlan?.plan?.paymentsType
              ?.split(",")
              .includes("CREDIT_CARD")
          : false;

        setEnableCreditCard(isCreditCard);
      }

      if (data?.order?.paymentType) {
        const isCreditCard = data?.order?.paymentType
          ? data?.order?.paymentType?.split(",").includes("CREDIT_CARD")
          : false;

        setEnableCreditCard(isCreditCard);
      }

      if (data?.creditCardInstallments && data?.billingType === "CREDIT_CARD") {
        setEnableCreditCard(true);
      }
    }
  }, [data]);

  useEffect(() => {
    const originalBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "#F7F7F7";

    return () => {
      document.body.style.backgroundColor = originalBg;
    };
  }, []);

  // Detecta transição para status confirmado enquanto aguardando PIX
  const CONFIRMED_STATUSES = [
    "CONFIRMED",
    "CONFIRMED_INSTALLMENT",
    "PAID",
    "RECEIVED_IN_CASH",
  ];

  useEffect(() => {
    const isNowConfirmed = CONFIRMED_STATUSES.includes(data?.status);
    const wasConfirmed = CONFIRMED_STATUSES.includes(
      prevStatusRef.current ?? "",
    );

    if (selectedPayment === "PIX" && isNowConfirmed && !wasConfirmed) {
      toast.success("Pagamento confirmado com sucesso!");
      setFullScreenPayment(false);
      setSelectedPayment("");
    }

    // Detecta confirmação de cartão via socket (PENDING → CONFIRMED)
    if (
      data?.billingType === "CREDIT_CARD" &&
      isNowConfirmed &&
      !wasConfirmed &&
      prevStatusRef.current === "PENDING"
    ) {
      toast.success("Pagamento confirmado com sucesso!");
    }

    // Detecta rejeição de cartão via socket (PENDING → ERROR)
    if (
      data?.billingType === "CREDIT_CARD" &&
      data?.status === "ERROR" &&
      prevStatusRef.current === "PENDING"
    ) {
      toast.error("Pagamento recusado. Verifique os dados do cartão.");
    }

    prevStatusRef.current = data?.status;
  }, [data?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Conecta ao socket quando o storeId estiver disponível.
  // Escuta `payment_confirmed` filtrando pelo webToken desta cobrança.
  // Cobre tanto cartão de crédito quanto PIX — qualquer confirmação via webhook
  // do backend chega aqui e dispara um refetch imediato.
  useEffect(() => {
    const storeId = data?.store?.id;
    if (!storeId) return;

    const socket = initializeSocket(String(storeId));

    socket.on("payment_confirmed", (payload: any) => {
      if (payload?.webToken !== param?.webToken) return;

      refetch();
    });

    return () => {
      socket.off("payment_confirmed");
      socket.disconnect();
    };
  }, [data?.store?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Encerramento temporário: checkout desativado.
  // Fluxo original permanece no arquivo para reativação futura.
  if (true) {
    return (
      <ThemeProvider theme={checkoutTheme}>
        <ContainerLoading maxWidth={"xs"} sx={{ width: "100%", padding: "0" }}>
          <BoxLoading
            sx={{
              minHeight: "100vh",
              position: "relative",
              backgroundColor: "#fff",
              minWidth: "100%",
            }}
            light
            loading={false}
          >
            <Stack
              justifyContent={"center"}
              alignItems={"center"}
              height={"100vh"}
              width={"100%"}
              textAlign={"center"}
              gap={2}
              px={3}
            >
              <Typography fontSize={56}>⚠️</Typography>
              <Typography variant="h5" fontWeight={700} color="#333">
                Estamos finalizando as atividades
              </Typography>
              <Typography variant="body1" color="#666" maxWidth={420}>
                Os serviços do FightGo estão sendo finalizados. Para tratar
                qualquer pendência, entre em contato com o suporte.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  window.open(
                    "https://wa.me/551926260132?text=Preciso%20de%20suporte",
                  );
                }}
              >
                Entrar em contato com suporte
              </Button>
            </Stack>
          </BoxLoading>
        </ContainerLoading>
      </ThemeProvider>
    );
  }

  const paymentMethodBoxStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#EFEFEF",
    borderRadius: "14px",
    padding: "12px 16px",
    cursor: "pointer",
    width: "100%",
    maxWidth: "500px",
    margin: "0 auto",
    "&:hover": {
      backgroundColor: "#ebebeb",
    },
  };

  return (
    <ThemeProvider theme={checkoutTheme}>
      <ContainerLoading maxWidth={"xs"} sx={{ width: "100%", padding: "0" }}>
        <BoxLoading
          sx={{
            minHeight: "100vh",
            position: "relative",
            backgroundColor: "#fff",
            minWidth: "100%",
          }}
          light
          loading={isLoading || isFetching || isPending || isRegeneratingPix}
        >
          {error ? (
            <Stack
              justifyContent={"center"}
              alignItems={"center"}
              height={"100vh"}
              width={"100%"}
              textAlign={"center"}
            >
              <Typography variant="h1" color="primary" fontWeight={700}>
                Ops!
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} mt={1}>
                Aconteceu um erro.
              </Typography>
              <Typography variant="subtitle1" fontWeight={700}>
                Cobrança não encontrada.
              </Typography>
              <Button
                variant="contained"
                sx={{ mt: 4 }}
                size="large"
                onClick={() => {
                  window.open(
                    "https://wa.me/551926260132?text=Preciso%20de%20suporte",
                  );
                }}
              >
                Entrar em contato com suporte
              </Button>
            </Stack>
          ) : !data?.mpPublicKey && !isLoading ? (
            <Stack
              justifyContent={"center"}
              alignItems={"center"}
              height={"100vh"}
              width={"100%"}
              textAlign={"center"}
              gap={2}
              px={3}
            >
              <Typography fontSize={56}>⚠️</Typography>
              <Typography variant="h5" fontWeight={700} color="#333">
                Pagamento indisponível
              </Typography>
              <Typography variant="body1" color="#666" maxWidth={320}>
                Esta academia ainda não conectou uma conta do Mercado Pago.
                Entre em contato com o suporte para regularizar a situação e
                liberar o pagamento.
              </Typography>
              <Button
                variant="contained"
                size="large"
                sx={{ mt: 2 }}
                onClick={() => {
                  window.open(
                    "https://wa.me/551926260132?text=Preciso%20de%20suporte",
                  );
                }}
              >
                Falar com suporte
              </Button>
            </Stack>
          ) : (
            <Grid2
              container
              spacing={1}
              mt={2}
              sx={{
                backgroundColor: "#fefdfd",
                maxWidth: "550px",
                width: "100%",
                padding: "0.6rem",
                paddingTop: "1rem",
              }}
              borderRadius={2}
              flexDirection={"column"}
              justifyContent={"center"}
              alignContent={"center"}
              margin={"auto"}
            >
              {fullScreenPayment ? (
                <>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ pb: "0.7rem" }}
                    width={"100%"}
                  >
                    <Stack justifyContent={"center"} alignContent={"center"}>
                      <Button
                        onClick={() => {
                          setFullScreenPayment(false);
                          setSelectedPayment("");
                        }}
                        startIcon={<IconArrowLeft color="black" size={18} />}
                        sx={{
                          color: "black",
                          fontSize: 13,
                          fontWeight: 600,
                          textTransform: "none",
                          px: 1,
                        }}
                      >
                        Voltar
                      </Button>
                    </Stack>

                    <Stack>
                      <img
                        src={logo}
                        style={{
                          width: "32px",
                          borderRadius: "10px",
                          height: "32px",
                        }}
                      />
                    </Stack>

                    <Stack sx={{ width: "32px" }} />
                  </Stack>
                  <Stack>
                    <Divider color="#EFEFEF" />

                    <Stack
                      alignItems="center"
                      justifyContent="space-between"
                      flexDirection={"row"}
                      my={1}
                    >
                      <Stack
                        alignItems="center"
                        justifyContent="center"
                        gap={1}
                        flexDirection={"row"}
                      >
                        <Stack
                          alignItems="center"
                          justifyContent="center"
                          sx={{
                            backgroundColor: "#EFEFEF",
                            padding: "0.4rem",
                            borderRadius: "100%",
                          }}
                        >
                          <IconBuildingStore color="black" size={14} />
                        </Stack>
                        <Stack>
                          <Typography
                            color="black"
                            fontWeight={700}
                            fontSize="14px"
                            noWrap
                            sx={{ textTransform: "uppercase" }}
                          >
                            {store?.name}
                          </Typography>
                        </Stack>
                      </Stack>

                      <Stack direction="row" justifyContent="center">
                        <Typography
                          color="black"
                          sx={{
                            fontSize: "14px",
                            fontWeight: 700,
                            color: "#7600ED",
                          }}
                        >
                          {formatPrice(data?.value)}
                        </Typography>
                      </Stack>
                    </Stack>
                    <Divider color="#EFEFEF" />
                  </Stack>
                </>
              ) : (
                <Box width={"100%"}>
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    flexDirection={"row"}
                    sx={{ pb: "1rem" }}
                  >
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        backgroundColor: "#EFEFEF",
                        padding: "0.6rem",
                        borderRadius: "100%",
                      }}
                    >
                      <IconBuildingStore color="black" size={18} />
                    </Stack>
                    <Stack>
                      <Typography
                        color="black"
                        fontWeight={700}
                        fontSize="14px"
                        noWrap
                        sx={{ textTransform: "uppercase" }}
                      >
                        {store?.name}
                      </Typography>
                      <Typography
                        color="black"
                        fontWeight={500}
                        fontSize="14px"
                        noWrap
                      >
                        {formatCpfCnpj(store?.document)}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Divider color="#EFEFEF" />

                  <Grid2
                    size={12}
                    sx={{ backgroundColor: getColor() }}
                    padding={1}
                    borderRadius={2}
                  >
                    {data?.billingType === "CREDIT_CARD" &&
                      data?.status === "PENDING" &&
                      data?.bankId && (
                        <Typography fontWeight={700} fontSize={12}>
                          Processando pagamento
                        </Typography>
                      )}

                    {!(
                      data?.billingType === "CREDIT_CARD" &&
                      data?.status === "PENDING" &&
                      data?.bankId
                    ) &&
                      ![
                        "CONFIRMED",
                        "CONFIRMED_INSTALLMENT",
                        "PAID",
                        "RECEIVED_IN_CASH",
                      ].includes(data?.status) && (
                        <Typography fontWeight={700} fontSize={12}>
                          Fatura{" "}
                          {formatPaymentStatus(
                            data?.status === "CONFIRMED"
                              ? "PAID"
                              : data?.status,
                          )}
                        </Typography>
                      )}

                    {[
                      "CONFIRMED",
                      "CONFIRMED_INSTALLMENT",
                      "PAID",
                      "RECEIVED_IN_CASH",
                    ].includes(data?.status) && (
                      <Typography fontWeight={700} fontSize={12}>
                        Fatura paga em {formatDate(data?.paymentDate)}
                      </Typography>
                    )}
                  </Grid2>
                </Box>
              )}

              {!fullScreenPayment && (
                <Stack
                  flexDirection={"column"}
                  justifyContent={"space-between"}
                  gap={1}
                  mt={6}
                  mb={6}
                >
                  <Stack alignItems={"center"} justifyContent="center">
                    <Typography
                      color="black"
                      sx={{ fontSize: "14px", fontWeight: 500 }}
                    >
                      Valor da cobrança
                    </Typography>
                    <Typography
                      color="black"
                      sx={{ fontSize: "34px", fontWeight: 700, mt: "1px" }}
                    >
                      {formatPrice(data?.value)}
                    </Typography>
                  </Stack>
                </Stack>
              )}

              {![
                "CONFIRMED",
                "PAID",
                "CONFIRMED_INSTALLMENT",
                "RECEIVED_IN_CASH",
              ].includes(data?.status) && (
                <>
                  {/* Fatura cancelada */}
                  {data?.status === "CANCELED" && (
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      spacing={2}
                      py={4}
                      px={2}
                      sx={{
                        backgroundColor: "#fff5f5",
                        border: "1px solid #ffd0d0",
                        borderRadius: "12px",
                        width: "100%",
                      }}
                    >
                      <Typography fontSize={48}>🚫</Typography>
                      <Typography
                        color="#B22222"
                        fontWeight={700}
                        fontSize={18}
                        textAlign="center"
                      >
                        Fatura cancelada
                      </Typography>
                      <Typography
                        color="#666"
                        fontSize={13}
                        textAlign="center"
                        maxWidth={300}
                      >
                        Esta cobrança foi cancelada e não pode ser paga. Entre
                        em contato com a academia caso tenha dúvidas.
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          window.open(
                            "https://wa.me/551926260132?text=Preciso%20de%20suporte",
                          );
                        }}
                        sx={{
                          mt: 1,
                          color: "#B22222",
                          borderColor: "#B22222",
                          fontSize: 12,
                          textTransform: "none",
                          borderRadius: "8px",
                        }}
                      >
                        Falar com suporte
                      </Button>
                    </Stack>
                  )}

                  {data?.status !== "CANCELED" && (
                    <>
                      {/* Cartão em processamento — aguardando confirmação via webhook */}
                      {!fullScreenPayment &&
                        data?.billingType === "CREDIT_CARD" &&
                        data?.status === "PENDING" &&
                        data?.bankId && (
                          <Stack
                            alignItems="center"
                            justifyContent="center"
                            spacing={2}
                            py={4}
                            px={2}
                          >
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                backgroundColor: "#7600ED",
                                animation: "pulse 1.5s ease-in-out infinite",
                                "@keyframes pulse": {
                                  "0%, 100%": { opacity: 1 },
                                  "50%": { opacity: 0.3 },
                                },
                              }}
                            />
                            <Typography
                              color="black"
                              fontWeight={700}
                              fontSize={16}
                              textAlign="center"
                            >
                              Processando pagamento. Dependendo do cartão pode
                              levar até 6 horas para o banco confirmar.
                              Agradecemos sua paciência!
                            </Typography>
                            <Typography
                              color="#575757"
                              fontSize={13}
                              textAlign="center"
                              maxWidth={300}
                            >
                              Seu pagamento foi enviado e está sendo analisado
                              pelo banco. Isso pode levar alguns instantes.
                            </Typography>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.5}
                            >
                              <IconLock size={14} color="#7600ED" />
                              <Typography fontSize={11} color="#888">
                                Transação segura via Mercado Pago
                              </Typography>
                            </Stack>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                setSelectedPayment("");
                                refetch();
                              }}
                              sx={{
                                mt: 1,
                                color: "#555",
                                borderColor: "#ccc",
                                fontSize: 12,
                                textTransform: "none",
                                borderRadius: "8px",
                              }}
                            >
                              Tentar com outro método de pagamento
                            </Button>
                          </Stack>
                        )}

                      {!fullScreenPayment &&
                      !(
                        data?.billingType === "CREDIT_CARD" &&
                        data?.status === "PENDING" &&
                        data?.bankId
                      ) ? (
                        <Stack spacing={1} sx={{ width: "100%" }}>
                          {enableCreditCard && (
                            <Box
                              onClick={() => {
                                setSelectedPayment("CREDIT_CARD");
                                setFullScreenPayment(true);
                              }}
                              sx={paymentMethodBoxStyle}
                            >
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <IconCreditCard
                                  color="#7600ED"
                                  style={{ width: "20px", height: "20px" }}
                                />
                                <Box>
                                  <Typography
                                    fontWeight={600}
                                    fontSize={"16px"}
                                    color="black"
                                  >
                                    Cartão de crédito
                                  </Typography>
                                </Box>
                              </Stack>
                              <IconArrowRight color="#7600ED" />
                            </Box>
                          )}

                          <Box
                            onClick={handleSelectPix}
                            sx={{
                              ...paymentMethodBoxStyle,
                              opacity: isRegeneratingPix ? 0.6 : 1,
                              pointerEvents: isRegeneratingPix
                                ? "none"
                                : "auto",
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              {/* <Box sx={iconCircleStyle}>
                            <img
                              src={Piximg}
                              alt="Pix"
                              style={{ width: "20px", height: "20px" }}
                            />
                          </Box> */}

                              <IconCurrencyReal
                                color="#7600ED"
                                style={{ width: "20px", height: "20px" }}
                              />
                              <Box>
                                <Typography
                                  fontWeight={600}
                                  fontSize={"16px"}
                                  color="black"
                                >
                                  Pagar com Pix
                                </Typography>
                              </Box>
                            </Stack>
                            <IconArrowRight color="#7600ED" />
                          </Box>
                        </Stack>
                      ) : (
                        <Stack
                          sx={{
                            width: "100%",
                            minHeight: "50vh",
                            backgroundColor: "#fff",
                          }}
                        >
                          {selectedPayment === "PIX" && (
                            <Stack
                              direction="column"
                              sx={{
                                padding: "5px",
                                mt: 2,
                                mb: 2,
                              }}
                            >
                              <Typography
                                fontSize={"18px"}
                                color="black"
                                fontWeight={700}
                              >
                                {selectedPayment === "PIX"
                                  ? "Pagar com Pix"
                                  : ""}
                              </Typography>
                              <Typography
                                sx={{
                                  mt: 0.5,
                                  fontSize: "14px",
                                }}
                                color="#575757"
                              >
                                Escaneie o QR code ou copie o código
                              </Typography>
                            </Stack>
                          )}

                          {selectedPayment === "PIX" &&
                            (() => {
                              const PIX_EXPIRATION_MINUTES = 60;
                              const pixCreatedAt =
                                data?.lastUpdatePixDate ?? data?.createdAt;
                              const isExpired = pixCreatedAt
                                ? moment(pixCreatedAt)
                                    .add(PIX_EXPIRATION_MINUTES, "minutes")
                                    .isBefore(moment())
                                : false;
                              const hasPixCode =
                                !!data?.bankPixCode && !isExpired;

                              return (
                                <Stack direction="column" gap={"32px"}>
                                  <Stack
                                    justifyContent="center"
                                    alignItems="center"
                                    spacing={2}
                                    sx={{
                                      flexGrow: 1,
                                      padding: "24px",
                                      border: "1px solid rgba(17, 17, 17, 0.1)",
                                      borderRadius: "16px",
                                      paddingY: "34px",
                                    }}
                                  >
                                    {hasPixCode ? (
                                      <QRCodeCanvas
                                        value={data.bankPixCode}
                                        style={{
                                          width: "170px",
                                          height: "170px",
                                        }}
                                      />
                                    ) : isExpired ? (
                                      <Stack alignItems="center" spacing={1}>
                                        <Typography fontSize={40}>
                                          ⏰
                                        </Typography>
                                        <Typography
                                          fontSize={14}
                                          color="#B22222"
                                          fontWeight={600}
                                        >
                                          QR Code expirado
                                        </Typography>
                                      </Stack>
                                    ) : (
                                      <Stack alignItems="center" spacing={1}>
                                        <Box
                                          sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: "50%",
                                            backgroundColor: "#7600ED",
                                            animation:
                                              "pulse 1.5s ease-in-out infinite",
                                            "@keyframes pulse": {
                                              "0%, 100%": { opacity: 1 },
                                              "50%": { opacity: 0.3 },
                                            },
                                          }}
                                        />
                                        <Typography
                                          fontSize={14}
                                          color="#575757"
                                        >
                                          Gerando QR Code...
                                        </Typography>
                                      </Stack>
                                    )}
                                  </Stack>

                                  {hasPixCode && (
                                    <Button
                                      variant="contained"
                                      fullWidth
                                      startIcon={<IconCopy />}
                                      onClick={async () => {
                                        await navigator.clipboard.writeText(
                                          data.bankPixCode,
                                        );
                                        toast.info("Código PIX copiado!");
                                      }}
                                      sx={{
                                        backgroundColor: "#7600ED",
                                        height: "2.2rem",
                                        fontSize: "14px",
                                      }}
                                    >
                                      Copiar código do QR Code
                                    </Button>
                                  )}

                                  {!hasPixCode && (
                                    <Button
                                      variant="outlined"
                                      fullWidth
                                      disabled={isRegeneratingPix}
                                      onClick={() => regeneratePix()}
                                      sx={{
                                        borderColor: "#7600ED",
                                        color: "#7600ED",
                                        height: "2.2rem",
                                      }}
                                    >
                                      {isRegeneratingPix
                                        ? "Gerando..."
                                        : "Gerar novo QR Code"}
                                    </Button>
                                  )}

                                  {data?.bankPixTicketUrl && hasPixCode && (
                                    <Button
                                      variant="outlined"
                                      fullWidth
                                      startIcon={<IconExternalLink size={16} />}
                                      onClick={() =>
                                        window.open(
                                          data.bankPixTicketUrl,
                                          "_blank",
                                        )
                                      }
                                      sx={{
                                        borderColor: "#7600ED",
                                        color: "#7600ED",
                                        height: "2.2rem",
                                        fontSize: "14px",
                                        "&:hover": {
                                          borderColor: "#5a00b8",
                                          backgroundColor:
                                            "rgba(118,0,237,0.04)",
                                        },
                                      }}
                                    >
                                      Abrir link de pagamento
                                    </Button>
                                  )}

                                  <Divider color="#EFEFEF" />

                                  <Stack sx={{ color: "black" }} spacing={1}>
                                    <Typography fontWeight={700} pb={1}>
                                      Instruções de Pagamento
                                    </Typography>
                                    <Typography
                                      fontSize={12}
                                      color="rgba(17, 17, 17, 0.7)"
                                    >
                                      1. Abra o aplicativo do seu banco;
                                    </Typography>
                                    <Typography
                                      fontSize={12}
                                      color="rgba(17, 17, 17, 0.7)"
                                    >
                                      2. Entre na área Pix e busque a opção de
                                      pagar com Pix Copia e Cola;
                                    </Typography>
                                    <Typography
                                      fontSize={12}
                                      color="rgba(17, 17, 17, 0.7)"
                                    >
                                      3. Insira o código copiado desta cobrança;
                                    </Typography>
                                    <Typography
                                      fontSize={12}
                                      color="rgba(17, 17, 17, 0.7)"
                                    >
                                      4. Revise o pagamento e confirme.
                                    </Typography>
                                    <Typography
                                      fontSize={12}
                                      color="rgba(17, 17, 17, 0.7)"
                                      pt={2}
                                    >
                                      Se estiver no computador, abra o
                                      aplicativo do seu banco e escaneie a
                                      imagem acima com a câmera do celular.
                                    </Typography>
                                  </Stack>

                                  {hasPixCode && (
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      justifyContent="center"
                                      spacing={1}
                                      sx={{
                                        mt: 1,
                                        mb: 1,
                                        padding: "10px 16px",
                                        borderRadius: "12px",
                                        backgroundColor: "rgba(118,0,237,0.06)",
                                        border:
                                          "1px solid rgba(118,0,237,0.15)",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: "50%",
                                          backgroundColor: "#7600ED",
                                          animation:
                                            "pulse 1.5s ease-in-out infinite",
                                          "@keyframes pulse": {
                                            "0%, 100%": { opacity: 1 },
                                            "50%": { opacity: 0.3 },
                                          },
                                        }}
                                      />
                                      <Typography
                                        fontSize={12}
                                        color="#7600ED"
                                        fontWeight={500}
                                      >
                                        Aguardando confirmação do pagamento...
                                      </Typography>
                                    </Stack>
                                  )}
                                </Stack>
                              );
                            })()}

                          {selectedPayment === "CREDIT_CARD" &&
                            enableCreditCard && (
                              <Stack>
                                <Stack
                                  direction="column"
                                  sx={{ padding: "5px", mb: 2, mt: 2 }}
                                >
                                  <Typography
                                    fontSize={"18px"}
                                    color="black"
                                    fontWeight={700}
                                  >
                                    Pagar com Cartão
                                  </Typography>
                                  <Typography
                                    sx={{ mt: 0.5, fontSize: "14px" }}
                                    color="#575757"
                                  >
                                    Preencha os dados abaixo para realizar o
                                    pagamento
                                  </Typography>
                                </Stack>

                                <MpCardForm
                                  publicKey={data.mpPublicKey}
                                  amount={data?.value ?? 0}
                                  maxInstallments={Number(
                                    data?.order?.installments ||
                                      data?.creditCardInstallments ||
                                      1,
                                  )}
                                  showSaveCreditCard={Boolean(
                                    !data?.studentOnPlan,
                                  )} //Por enquanto não tem como salvar cartão
                                  saveCreditCard={saveCreditCard}
                                  defaultEmail={
                                    data?.payer?.email ??
                                    data?.user?.email ??
                                    ""
                                  }
                                  onToggleSaveCreditCard={() =>
                                    setSaveCreditCard(!saveCreditCard)
                                  }
                                  onSubmit={({
                                    cardToken,
                                    paymentMethodId,
                                    issuerId,
                                    installments,
                                    saveCreditCard: sc,
                                    cardholderName: ch,
                                    lastFourDigits,
                                    expirationMonth,
                                    expirationYear,
                                    deviceId,
                                    payerEmail,
                                  }) => {
                                    mutate({
                                      webToken: param?.webToken,
                                      cardToken,
                                      paymentMethodId,
                                      issuerId,
                                      installments,
                                      saveCreditCard: sc,
                                      cardholderName: ch,
                                      lastFourDigits,
                                      expirationMonth,
                                      expirationYear,
                                      deviceId,
                                      payerEmail,
                                    });
                                  }}
                                  isPending={isPending}
                                />
                              </Stack>
                            )}
                        </Stack>
                      )}
                    </>
                  )}
                </>
              )}

              {!fullScreenPayment && (
                <>
                  <Divider color="#EFEFEF" sx={{ mt: 2 }} />
                  <Stack mt={2}>
                    <Stack
                      mb={"12px"}
                      direction="row"
                      alignItems={"center"}
                      gap={"6px"}
                    >
                      <IconWallet size={20} color="black" />
                      <Typography
                        fontWeight={700}
                        fontSize={"14px"}
                        color="black"
                      >
                        Detalhes da cobrança
                      </Typography>
                    </Stack>
                    <Stack
                      mb={"10px"}
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Typography
                        fontWeight={700}
                        fontSize={"14px"}
                        color="black"
                      >
                        Nome
                      </Typography>
                      <Typography color="black" fontSize={"14px"}>
                        {data?.payer?.fullName}
                      </Typography>
                    </Stack>
                    <Stack
                      mb={"10px"}
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Typography
                        fontWeight={700}
                        fontSize={"14px"}
                        color="black"
                      >
                        Vencimento
                      </Typography>
                      <Typography fontSize={"14px"} color="black">
                        {formatDate(data?.dueDate)}
                      </Typography>
                    </Stack>

                    <Stack direction="column" justifyContent="space-between">
                      <Typography
                        fontWeight={700}
                        fontSize={"14px"}
                        color="black"
                      >
                        Descrição
                      </Typography>
                      <Typography color="black" fontSize={"14px"} mt={"4px"}>
                        {data?.description}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Divider color="#EFEFEF" sx={{ mt: 2 }} />
                </>
              )}
            </Grid2>
          )}

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{
              position: "sticky",
              bottom: 0,
              left: 0,
              width: "100%",
              backgroundColor: "#F0F1F5",
              padding: "1rem",
            }}
          >
            <IconLock size={16} color="#333" />
            <Typography
              fontWeight={700}
              ml={0.5}
              fontSize={"14px"}
              color="black"
            >
              Desenvolvido por Fight Go
            </Typography>
          </Stack>
        </BoxLoading>
      </ContainerLoading>
    </ThemeProvider>
  );
};

export default Checkout;
